// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect } from 'react';
import { useI18n } from '../i18n';

export const applyTheme = (theme) => {
  const root = document.documentElement;
  const apply = (isDark) => root.setAttribute('data-theme', isDark ? 'dark' : 'light');

  if (theme === 'system') {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    apply(mq.matches);
    // Listener pour changer automatiquement si l'OS change en cours de session
    const handler = (e) => {
      if (localStorage.getItem('norma_theme') === 'system') apply(e.matches);
    };
    mq.removeEventListener('change', handler); // éviter les doublons
    mq.addEventListener('change', handler);
  } else {
    apply(theme === 'dark');
  }
};

export const initTheme = () => {
  const saved = localStorage.getItem('norma_theme') || 'light';
  applyTheme(saved);
};
const Section = ({ title, children }) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );

const OptionCard = ({ value, current, onChange, icon, label, sub }) => (
  <div
    onClick={() => onChange(value)}
    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${current === value ? 'var(--blue)' : 'var(--gray)'}`, background: current === value ? 'rgba(59,130,246,0.08)' : 'var(--white)', cursor: 'pointer', transition: 'all 0.15s', marginBottom: 8 }}
  >
    <span style={{ fontSize: 20 }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--black)' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>}
    </div>
    <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${current === value ? 'var(--blue)' : 'var(--gray)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {current === value && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--blue)' }} />}
    </div>
  </div>
);
const PreferencesModal = ({ onClose }) => {
  const { t, lang, setLang } = useI18n();
  const [theme, setTheme] = useState(localStorage.getItem('norma_theme') || 'light');
  const [language, setLanguage] = useState(lang);

  // Preview thème en temps réel
  useEffect(() => { applyTheme(theme); }, [theme]);

  const save = () => {
    localStorage.setItem('norma_theme', theme);
    setLang(language);
    applyTheme(theme);
    onClose();
  };

  const cancel = () => {
    const saved = localStorage.getItem('norma_theme') || 'light';
    applyTheme(saved);
    onClose();
  };

  

  return (
    <div onClick={cancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--white)', borderRadius: 14, width: '100%', maxWidth: 400, boxShadow: 'var(--shadow-md)', overflow: 'hidden', border: '1px solid var(--gray)' }}>

        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--gray)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)' }}>⚙️ {t('pref_title')}</div>
          <button onClick={cancel} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'rgba(128,128,128,0.15)', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--black)' }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <Section title={t('pref_theme')}>
            <OptionCard value="light"  current={theme} onChange={setTheme} icon="☀️" label={t('pref_light')}  sub={t('pref_light_sub')} />
            <OptionCard value="dark"   current={theme} onChange={setTheme} icon="🌙" label={t('pref_dark')}   sub={t('pref_dark_sub')} />
            <OptionCard value="system" current={theme} onChange={setTheme} icon="💻" label={t('pref_system')} sub={t('pref_system_sub')} />
          </Section>

          <Section title={t('pref_lang')}>
            <OptionCard value="fr" current={language} onChange={setLanguage} icon="🇫🇷" label="Français" sub={t('pref_fr_sub')} />
            <OptionCard value="en" current={language} onChange={setLanguage} icon="🇬🇧" label="English"  sub={t('pref_en_sub')} />
          </Section>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={cancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, background: 'transparent', border: '1px solid var(--gray)', fontSize: 14, cursor: 'pointer', color: 'var(--black)' }}>
              {t('pref_cancel')}
            </button>
            <button onClick={save} style={{ flex: 1, padding: '10px 0', borderRadius: 8, background: 'var(--blue)', color: 'white', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-light)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--blue)'}
            >
              {t('pref_save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreferencesModal;